import { TZDate } from "@date-fns/tz";

/**
 * Conversions between a stored instant and the wall-clock string a date or
 * date-time form field holds.
 *
 * ## Why this exists
 *
 * `"2026-09-05T10:00"` carries no timezone, so `new Date(value)` resolves it
 * against **whatever runtime evaluates it**. The event editor used to format
 * that string on the server (`getEventForEdit`) and parse it on the client
 * (`eventUpdateSchema.safeParse` runs inside `onSubmit`, in the browser), which
 * meant the two ends disagreed by `serverOffset - browserOffset`. Locally,
 * where the dev server and the browser share a timezone, the round trip looked
 * clean. On Vercel the server is UTC, so an event stored at 10:00 IST was shown
 * to the queen as 04:30 and every save moved it 5h30m earlier — compounding on
 * each save.
 *
 * Every function here takes an explicit `timeZone`, so the result never depends
 * on where the code runs. Pass the same zone on both ends of a round trip and
 * it is lossless.
 *
 * ## Which zone
 *
 * For anything a venue schedules, that is `EVENT_FORM_TIME_ZONE` — the same
 * zone `expandRecurrenceRule` materializes occurrences in. Editing one
 * occurrence by hand and generating it from a weekly rule then agree, and a
 * queen who travels does not silently reschedule an event by opening the
 * editor.
 */

/**
 * The zone the product treats as "local" when nothing more specific applies.
 *
 * Hiive is India-first — prices are en-IN, recurrence rules default here — so
 * this is the sensible fallback for a UI control that needs a calendar date
 * before it knows which entity it belongs to. Anything that stores a wall clock
 * should name its own zone instead (see `EVENT_FORM_TIME_ZONE`).
 */
export const APP_DEFAULT_TIME_ZONE = "Asia/Kolkata";

/** `"YYYY-MM-DDTHH:mm"` — the value shape of a `datetime-local` field. */
export type DateTimeInputValue = string;

/** `"YYYY-MM-DD"` — the value shape of a `date` field. */
export type DateInputValue = string;

const DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Does this string already pin itself to a point on the timeline?
 *
 * A trailing `Z` or a `±HH:mm` offset means the value is a real instant and
 * must be respected as-is. Only a bare wall clock is ours to interpret. This is
 * what lets the zoned schemas stay backward-compatible with callers that
 * already send proper ISO instants.
 */
export function hasExplicitTimeZone(value: string): boolean {
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value.trim());
}

function pad2(value: number) {
  return String(value).padStart(2, "0");
}

/**
 * The wall clock an instant shows in `timeZone`, as a `datetime-local` value.
 * Seconds are dropped, matching what the input can represent.
 */
export function toZonedDateTimeInput(
  instant: Date | null | undefined,
  timeZone: string,
): DateTimeInputValue {
  if (!instant || Number.isNaN(instant.getTime())) return "";

  const zoned = new TZDate(instant.getTime(), timeZone);
  return `${zoned.getFullYear()}-${pad2(zoned.getMonth() + 1)}-${pad2(zoned.getDate())}T${pad2(zoned.getHours())}:${pad2(zoned.getMinutes())}`;
}

/** The calendar date an instant falls on in `timeZone`, as a `date` value. */
export function toZonedDateInput(
  instant: Date | null | undefined,
  timeZone: string,
): DateInputValue {
  if (!instant || Number.isNaN(instant.getTime())) return "";

  const zoned = new TZDate(instant.getTime(), timeZone);
  return `${zoned.getFullYear()}-${pad2(zoned.getMonth() + 1)}-${pad2(zoned.getDate())}`;
}

/**
 * The instant at which `timeZone` shows this wall clock. Returns null for
 * anything that is not a well-formed value, so callers can distinguish "empty
 * or malformed" from a real date.
 *
 * A value that already carries an offset or `Z` is parsed as the instant it
 * states, ignoring `timeZone`.
 */
export function fromZonedDateTimeInput(
  value: string | null | undefined,
  timeZone: string,
): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (hasExplicitTimeZone(trimmed)) {
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = DATE_TIME_PATTERN.exec(trimmed);
  if (!match) {
    // Not a wall clock we recognise — a date-only value is still meaningful.
    return fromZonedDateInput(trimmed, timeZone);
  }

  const [, year, month, day, hours, minutes] = match;
  return zonedPartsToInstant(
    {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hours: Number(hours),
      minutes: Number(minutes),
    },
    timeZone,
  );
}

/**
 * The instant at local midnight of this calendar date in `timeZone`. Midnight
 * rather than noon so a date-only field round-trips through
 * `toZonedDateInput` unchanged.
 */
export function fromZonedDateInput(
  value: string | null | undefined,
  timeZone: string,
): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (hasExplicitTimeZone(trimmed)) {
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = DATE_PATTERN.exec(trimmed);
  if (!match) return null;

  const [, year, month, day] = match;
  return zonedPartsToInstant(
    {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hours: 0,
      minutes: 0,
    },
    timeZone,
  );
}

function zonedPartsToInstant(
  parts: {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
  },
  timeZone: string,
): Date | null {
  if (parts.month < 1 || parts.month > 12) return null;
  if (parts.day < 1 || parts.day > 31) return null;
  if (parts.hours > 23 || parts.minutes > 59) return null;

  const zoned = new TZDate(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    0,
    0,
    timeZone,
  );

  const time = zoned.getTime();
  if (Number.isNaN(time)) return null;

  // Reject a rolled-over date ("2026-02-31" becoming 3 March) rather than
  // silently storing the wrong day.
  if (
    zoned.getFullYear() !== parts.year ||
    zoned.getMonth() !== parts.month - 1 ||
    zoned.getDate() !== parts.day
  ) {
    return null;
  }

  // TZDate extends Date but carries its zone through formatting; hand back a
  // bare instant so drizzle's timestamp mode and the wire format see a plain
  // Date.
  return new Date(time);
}

/**
 * Splits a `datetime-local` value into the two halves a calendar-plus-time
 * field edits. Neither half is timezone-dependent — this is string surgery.
 */
export function splitDateTimeInput(value: string | null | undefined): {
  date: DateInputValue;
  time: string;
} {
  const trimmed = (value ?? "").trim();
  const match = DATE_TIME_PATTERN.exec(trimmed);
  if (!match) {
    return { date: DATE_PATTERN.test(trimmed) ? trimmed : "", time: "" };
  }

  const [, year, month, day, hours, minutes] = match;
  return { date: `${year}-${month}-${day}`, time: `${hours}:${minutes}` };
}

/** Rejoins the halves. Returns "" unless there is at least a date. */
export function joinDateTimeInput(date: string, time: string): string {
  const trimmedDate = date.trim();
  if (!DATE_PATTERN.test(trimmedDate)) return "";

  const trimmedTime = time.trim();
  const normalizedTime = /^(\d{1,2}):(\d{2})$/.test(trimmedTime)
    ? `${pad2(Number(trimmedTime.split(":")[0]))}:${trimmedTime.split(":")[1]}`
    : "00:00";

  return `${trimmedDate}T${normalizedTime}`;
}

const DISPLAY_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Day-first display for a `date` value: `"2026-09-05"` → `"05/09/2026"`.
 *
 * A native `date` input renders in the *browser's* locale, which showed
 * 5 September as `09/05/2026` to anyone on an en-US browser — indistinguishable
 * from 9 May. There is no HTML attribute that changes it, which is the reason
 * these fields are rendered as a custom control.
 */
export function formatDateInputDayFirst(value: string | null | undefined) {
  const match = DATE_PATTERN.exec((value ?? "").trim());
  if (!match) return "";

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

/** `"2026-09-05"` → `"05 Sep 2026"`, for a label with no ambiguity at all. */
export function formatDateInputLong(value: string | null | undefined) {
  const match = DATE_PATTERN.exec((value ?? "").trim());
  if (!match) return "";

  const [, year, month, day] = match;
  return `${day} ${DISPLAY_MONTHS[Number(month) - 1]} ${year}`;
}

/** `"14:30"` → `"2:30 pm"`. Empty string for anything unparseable. */
export function formatTimeInput12Hour(value: string | null | undefined) {
  const match = /^(\d{1,2}):(\d{2})$/.exec((value ?? "").trim());
  if (!match) return "";

  const hours = Number(match[1]);
  if (hours > 23) return "";

  const suffix = hours < 12 ? "am" : "pm";
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${match[2]} ${suffix}`;
}

/** `"2026-09-05T14:30"` → `"05 Sep 2026, 2:30 pm"`. */
export function formatDateTimeInputLong(value: string | null | undefined) {
  const { date, time } = splitDateTimeInput(value);
  const datePart = formatDateInputLong(date);
  if (!datePart) return "";

  const timePart = formatTimeInput12Hour(time);
  return timePart ? `${datePart}, ${timePart}` : datePart;
}
